import { CSSResult, html, LitElement, nothing, TemplateResult, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import styles from './filter.scss?inline';
import { sharedCss } from '../../../scripts/style-bundle/shared.js';
import { FilterMenu } from '../../../scripts/filter/filter-menu.js';
import { filterContext } from '../../../scripts/context/filterContext.js';
import { Filter } from '../../../scripts/filter/filter.js';
import baseLayerStyles from '../base-layer/base-layer.scss?inline';
import { createButtonLabelWithCount } from '../../../scripts/filter/filter-label.js';
import { i18nContext } from '../../../scripts/context/i18nContext.js';
import { Messages } from '../../../@types/wishlist.js';
import { hideIconFromAssistiveTechnology } from '../icon/icon-accessibility.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import arrowBackIcon from '@breuni-ui/bdk/design-tokens/images/icons/o-ic-16_arrow-left.svg?element';

// lazy load initially hidden components
import('./filter-overview.js');
import('./filter-group.js');

@customElement('ents-filter')
export class UIFilter extends LitElement {
  @property({ type: Boolean, attribute: 'curated-list' })
  private isCuratedList: boolean = false;

  @consume({ context: filterContext, subscribe: true })
  @property({ attribute: false })
  private filterMenu!: FilterMenu;

  @consume({ context: i18nContext })
  private messages!: Messages;

  @state()
  private activeFilter?: string;

  private shouldScrollOnClose: boolean = false;

  static get styles(): CSSResult[] {
    return [sharedCss, unsafeCSS(styles), unsafeCSS(baseLayerStyles)];
  }

  private setLayerClosed(resetActiveFilter = true) {
    this.dispatchEvent(new Event('revert', { bubbles: true, composed: true }));
    this.dispatchEvent(new Event('close'));
    if (resetActiveFilter) this.resetActiveFilter();
    this.checkAndResetScrollToTop();
  }

  private getOpenFilter(): Filter | undefined {
    return this.filterMenu.filter.find((f) => f.id === this.activeFilter);
  }

  protected render(): TemplateResult {
    const openFilter = this.getOpenFilter();

    return html`
      <ents-base-layer
        drawer-test-id="ents-filter-layer"
        ?showLayer="${this.filterMenu.isOpen}"
        @ents-layer-closed="${this.setLayerClosed}"
      >
        <div slot="header-start">${this.buildHeaderStart(openFilter)}</div>
        <div slot="title">${this.buildTitle(openFilter)}</div>
        <div>${this.buildContent(openFilter)}</div>
        <div slot="footer">${this.buildFooter(openFilter)}</div>
      </ents-base-layer>
    `;
  }

  private buildContent(openFilter?: Filter) {
    if (openFilter) {
      return html` <ents-filter-group
        .filter="${openFilter}"
        @apply="${this.onApply}"
        @close="${() => this.setLayerClosed(false)}"
      ></ents-filter-group>`;
    } else {
      return html` <ents-filter-overview
        ?curated-list="${this.isCuratedList}"
        @reset="${this.onReset}"
        @open-filter="${this.onOpenFilter}"
      ></ents-filter-overview>`;
    }
  }

  private buildHeaderStart(openFilter?: Filter) {
    if (!openFilter) {
      return nothing;
    }

    return html` <button
      class="ents-filter-group__back bdk-link-secondary"
      size="medium"
      aria-label="${this.messages['ents_wishlist_filter_back']}"
      data-test-ents-filter-group-top-button-back
      @click="${this.onApply}"
    >
      ${hideIconFromAssistiveTechnology(arrowBackIcon())}${this.messages['ents_wishlist_filter_back']}
    </button>`;
  }

  private buildTitle(openFilter?: Filter) {
    if (openFilter) {
      return html` <h3 class="ents-filter-group__title ents-heading-medium" data-test-ents-filter-heading>
        ${unsafeHTML(openFilter.label(this.messages))}
      </h3>`;
    } else {
      return html` <h3 class="ents-heading-medium" data-test-ents-filter-heading>
        ${this.messages['ents_wishlist_filter_headline']}
      </h3>`;
    }
  }

  private buildFooter(openFilter?: Filter) {
    if (!openFilter) {
      return html` <div class="ents-filter__footer">
        <button
          class="bdk-button"
          @click="${this.setLayerClosed}"
          variant="primary"
          size="medium"
          data-test-ents-filter-overview-bottom-button
        >
          ${createButtonLabelWithCount(this.messages, this.filterMenu.resultCount)}
        </button>
      </div>`;
    }

    if (!this.filterMenu.isEdited()) {
      return html` <button
        class="bdk-button"
        variant="outline"
        size="medium"
        full-width
        @click="${this.onRevert}"
        data-test-ents-filter-group-bottom-button-back
      >
        ${this.messages['ents_wishlist_filter_back']}
      </button>`;
    }

    return html` <button
      class="bdk-button"
      type="button"
      variant="primary"
      size="medium"
      full-width
      @click="${this.onApply}"
      data-test-ents-filter-group-bottom-button-submit
    >
      ${this.messages['ents_wishlist_filter_submit']}
    </button>`;
  }

  private onOpenFilter(e: CustomEvent<{ filterId: string }>) {
    this.activeFilter = e.detail.filterId;
  }

  private onRevert() {
    this.resetActiveFilter();
  }

  private onApply() {
    this.dispatchEvent(new Event('apply', { bubbles: true, composed: true }));
    this.resetActiveFilter();
    this.shouldScrollOnClose = true;
  }

  private onReset() {
    this.shouldScrollOnClose = true;
  }

  private resetActiveFilter() {
    this.activeFilter = undefined;
  }

  private checkAndResetScrollToTop() {
    if (this.shouldScrollOnClose) {
      this.shouldScrollOnClose = false;
      this.dispatchEvent(new Event('scroll-top'));
    }
  }
}
